/**
 * 날짜 유틸리티 함수
 * 로스트아크 컨텐츠 초기화 기준 (수요일 00:00)
 */

/**
 * 수요일 기준 주차 범위 계산
 * @param weekOffset 0: 이번주, -1: 지난주, 1: 다음주
 * @returns { wedDate: "2025-01-15", tueDate: "2025-01-21" }
 */
export function getWeekRange(weekOffset: number = 0): {
  wedDate: string;
  tueDate: string;
  dates: string[];  // 7일 날짜 배열 (수~화)
} {
  const today = new Date();
  const dayOfWeek = today.getDay();

  // 이번주 수요일 계산 (0=일, 3=수)
  const daysToWed = dayOfWeek >= 3 ? dayOfWeek - 3 : dayOfWeek + 4;
  const thisWednesday = new Date(today);
  thisWednesday.setDate(today.getDate() - daysToWed);
  thisWednesday.setHours(0, 0, 0, 0);

  // weekOffset 적용 (주 단위 이동)
  const targetWednesday = new Date(thisWednesday);
  targetWednesday.setDate(thisWednesday.getDate() + weekOffset * 7);

  // 화요일 계산 (수요일 + 6일)
  const targetTuesday = new Date(targetWednesday);
  targetTuesday.setDate(targetWednesday.getDate() + 6);

  // 7일 날짜 배열 생성 (수~화)
  const dates: string[] = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(targetWednesday);
    date.setDate(targetWednesday.getDate() + i);
    dates.push(formatDate(date));
  }

  return {
    wedDate: formatDate(targetWednesday),
    tueDate: formatDate(targetTuesday),
    dates,
  };
}

/**
 * Date를 "yyyy-MM-dd" 형식으로 변환
 */
export function formatDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Date를 "yyyy-MM-dd HH:mm" 형식으로 변환
 */
export function formatDateTime(date: Date): string {
  const dateStr = formatDate(date);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${dateStr} ${hours}:${minutes}`;
}

/**
 * "yyyy-MM-dd HH:mm:ss" 형식을 Date 객체로 변환
 */
export function parseDateTime(dateTimeStr: string): Date {
  // LocalDateTime 형식 처리 (T 구분자)
  if (dateTimeStr.includes('T')) {
    return new Date(dateTimeStr);
  }

  // "yyyy-MM-dd HH:mm:ss" 또는 "yyyy-MM-dd HH:mm" 형식
  const [datePart, timePart] = dateTimeStr.split(' ');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);

  return new Date(year, month - 1, day, hours, minutes);
}

/**
 * 날짜 문자열을 "MM/dd (요일)" 형식으로 표시
 */
export function formatDateDisplay(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const dayOfWeek = ['일', '월', '화', '수', '목', '금', '토'][date.getDay()];

  return `${month}/${day} (${dayOfWeek})`;
}

/**
 * 시간 문자열을 "HH:mm" 형식으로 표시
 */
export function formatTimeDisplay(dateTimeStr: string): string {
  const date = parseDateTime(dateTimeStr);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
}

/**
 * 현재 시간 기준으로 초기 주차 오프셋 계산
 * 월요일/화요일이면 다음주로 자동 이동
 */
export function getInitialWeekOffset(): number {
  const today = new Date();
  const dayOfWeek = today.getDay();

  // 월요일(1) 또는 화요일(2)이면 다음주로
  return dayOfWeek === 1 || dayOfWeek === 2 ? 1 : 0;
}

/**
 * weekOffset을 "지난주", "이번주", "다음주" 텍스트로 변환
 */
export function getWeekLabel(weekOffset: number): string {
  if (weekOffset < 0) return '지난주';
  if (weekOffset === 0) return '이번주';
  return '다음주';
}

/**
 * 날짜 문자열이 오늘인지 확인
 */
export function isToday(dateStr: string): boolean {
  const today = formatDate(new Date());
  return dateStr === today;
}

/**
 * 요일 이름 배열 (수~화 순서)
 */
export const WEEKDAY_NAMES = ['수요일', '목요일', '금요일', '토요일', '일요일', '월요일', '화요일'];
